// 📑 Tab System - Sistema de pestañas dinámico (reordenable por arrastre)
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronsRight, Columns2, MoreVertical, RotateCw, X, XSquare } from 'lucide-react';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { useTabsStore } from '@/stores/tabs.store';
import { motion, Reorder } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// Botón ↻ de la barra: refresca bajo demanda los datos de las pantallas montadas
// (fuerza el refetch de las consultas activas). Gira mientras haya alguna en curso.
function RefreshTabsButton() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const fetching = useIsFetching();
  return (
    <button
      type="button"
      onClick={() => { void qc.refetchQueries({ type: 'active' }); }}
      title={t('shell.refresh_screen_title')}
      aria-label={t('shell.refresh_screen')}
      className="ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-md border-l text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
    >
      <RotateCw className={cn('h-4 w-4', fetching > 0 && 'animate-spin')} />
    </button>
  );
}

// Estado del menú contextual: pantalla (x,y) + pestaña objetivo.
interface MenuState { x: number; y: number; tabId: string }

// Menú de acciones sobre pestañas (clic derecho en una pestaña o botón ⋯ de la barra).
// Se renderiza en un portal a document.body (fuera de la barra y de framer-motion) y
// dispara las acciones en onPointerDown, para que ningún apilamiento ni evento global
// intercepte el clic.
function TabContextMenu({ menu, onClose }: { menu: MenuState; onClose: () => void }) {
  const { t } = useTranslation();
  const { tabs, closeTab, closeOtherTabs, closeTabsToRight, closeAllTabs } = useTabsStore();

  const idx = tabs.findIndex((t) => t.id === menu.tabId);
  const target = tabs[idx];
  const cerrables = tabs.filter((t) => t.closable);
  const otrasCerrables = tabs.some((t) => t.id !== menu.tabId && t.closable);
  const hayDerecha = tabs.some((t, i) => i > idx && t.closable);

  // Cierra con Escape o al desplazar/redimensionar (el clic-fuera lo maneja el backdrop).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onClose);
    window.addEventListener('scroll', onClose, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onClose);
      window.removeEventListener('scroll', onClose, true);
    };
  }, [onClose]);

  if (!target) return null;

  const run = (fn: () => void) => { fn(); onClose(); };
  // Ubica el menú sin que se salga por el borde derecho/inferior de la ventana.
  const left = Math.min(menu.x, window.innerWidth - 232);
  const top = Math.min(menu.y, window.innerHeight - 176);

  const Item = ({ icon: Icon, label, disabled, onAct }: { icon: typeof X; label: string; disabled?: boolean; onAct: () => void }) => (
    <button
      type="button"
      disabled={disabled}
      onPointerDown={(e) => { e.preventDefault(); if (!disabled) run(onAct); }}
      className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-40"
    >
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="flex-1 truncate">{label}</span>
    </button>
  );

  return createPortal(
    <>
      {/* Backdrop transparente: cualquier clic afuera cierra el menú */}
      <div
        className="fixed inset-0 z-[998]"
        onPointerDown={onClose}
        onContextMenu={(e) => { e.preventDefault(); onClose(); }}
      />
      <div
        role="menu"
        style={{ left, top }}
        className="fixed z-[999] w-56 rounded-md border bg-popover p-1 shadow-md"
      >
        <div className="truncate px-2 py-1 text-xs text-muted-foreground">{target.title}</div>
        <Item icon={X} label="Cerrar esta" disabled={!target.closable} onAct={() => closeTab(menu.tabId)} />
        <Item icon={Columns2} label={t('shell.close_others')} disabled={!otrasCerrables} onAct={() => closeOtherTabs(menu.tabId)} />
        <Item icon={ChevronsRight} label="Cerrar a la derecha" disabled={!hayDerecha} onAct={() => closeTabsToRight(menu.tabId)} />
        <Item icon={XSquare} label="Cerrar todas" disabled={cerrables.length === 0} onAct={() => closeAllTabs()} />
      </div>
    </>,
    document.body
  );
}

export function TabSystem() {
  const { t } = useTranslation();
  const { tabs, activeTabId, setActiveTab, closeTab, reorderTabs } = useTabsStore();
  const activeRef = useRef<HTMLDivElement>(null);
  const [menu, setMenu] = useState<MenuState | null>(null);

  // Atajos de teclado para moverse entre pestañas:
  //   Ctrl+Alt+→ / Ctrl+Alt+←  → siguiente / anterior (con vuelta)
  //   Ctrl+Alt+W               → cerrar la pestaña activa
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.ctrlKey || !e.altKey) return;
      const st = useTabsStore.getState();
      if (st.tabs.length === 0) return;
      const cur = Math.max(0, st.tabs.findIndex((t) => t.id === st.activeTabId));
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        st.setActiveTab(st.tabs[(cur + 1) % st.tabs.length].id);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        st.setActiveTab(st.tabs[(cur - 1 + st.tabs.length) % st.tabs.length].id);
      } else if (e.key.toLowerCase() === 'w') {
        e.preventDefault();
        st.closeTab(st.activeTabId);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Desplazar la pestaña activa a la vista al cambiarla
  useEffect(() => {
    activeRef.current?.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
  }, [activeTabId]);

  return (
    <div className="flex h-12 items-center border-b bg-card pr-2">
    <Reorder.Group
      as="div"
      axis="x"
      values={tabs}
      onReorder={reorderTabs}
      className="flex h-full flex-1 items-center gap-1 px-2 overflow-x-auto scrollbar-thin"
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeTabId;

        return (
          <Reorder.Item
            as="div"
            key={tab.id}
            value={tab}
            ref={isActive ? activeRef : undefined}
            layout
            whileDrag={{ scale: 1.04, zIndex: 10, cursor: 'grabbing' }}
            title={`${tab.title} · arrastra para reordenar · Ctrl+Alt+←/→ para cambiar · clic derecho para más opciones`}
            data-tab
            aria-selected={isActive}
            onClick={() => setActiveTab(tab.id)}
            onContextMenu={(e: React.MouseEvent) => { e.preventDefault(); setActiveTab(tab.id); setMenu({ x: e.clientX, y: e.clientY, tabId: tab.id }); }}
            className={cn(
              'group relative flex h-9 min-w-[120px] max-w-[200px] shrink-0 cursor-grab items-center gap-2 rounded-md px-3 text-sm font-medium transition-colors active:cursor-grabbing',
              isActive
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
            )}
          >
            {/* Tab title */}
            <span className="truncate flex-1 select-none">{tab.title}</span>

            {/* Close button */}
            {tab.closable && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  closeTab(tab.id);
                }}
                onPointerDown={(e) => e.stopPropagation()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    closeTab(tab.id);
                  }
                }}
                className={cn(
                  'flex h-5 w-5 items-center justify-center rounded-sm opacity-0 transition-all group-hover:opacity-100 cursor-pointer',
                  isActive ? 'hover:bg-primary-foreground/20' : 'hover:bg-accent'
                )}
                aria-label={`Cerrar pestaña ${tab.title}`}
              >
                <X className="h-3 w-3" />
              </div>
            )}

            {/* Active indicator */}
            {isActive && (
              <motion.div
                layoutId="activeTab"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-foreground"
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              />
            )}
          </Reorder.Item>
        );
      })}
    </Reorder.Group>
      {/* Botón ⋯: abre el menú de acciones sobre la pestaña activa */}
      {tabs.length > 0 && (
        <button
          type="button"
          onClick={(e) => {
            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
            setMenu({ x: r.right - 224, y: r.bottom + 4, tabId: activeTabId });
          }}
          title={t('shell.tab_options_title')}
          aria-label={t('shell.tab_options')}
          className="ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-md border-l text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      )}
      <RefreshTabsButton />
      {menu && <TabContextMenu menu={menu} onClose={() => setMenu(null)} />}
    </div>
  );
}
